import React from 'react';
import { push } from 'redux-first-history';
import { connect } from '../../redux/store';
import {
  compose,
  lifecycle,
  withHandlers,
  withProps,
  withState,
} from 'recompose';
import { openModalForm, TimeAgo, Utils, C } from '@kineticdata/bundle-common';
import { I18n } from '@kineticdata/react';
import { Modal, ModalBody } from 'reactstrap';
import { actions } from '../../redux/modules/submission';
import { expandSLTValue, parsePVGOverrideAttribute } from '../../utils';
import { Countdown } from '../shared/Countdown';
import classNames from 'classnames';

const DisplayDateItem = ({ submission }) => {
  const isDraft = submission.coreState === C.CORE_STATE_DRAFT;
  const sameByAndFor =
    !!submission.values[C.FIELD_REQUESTED_BY_NAME] &&
    submission.values[C.FIELD_REQUESTED_BY_NAME] ===
      submission.values[C.FIELD_REQUESTED_FOR_NAME];
  return (
    <>
      <TimeAgo
        timestamp={isDraft ? submission.createdAt : submission.submittedAt}
      />
      {!!submission.values[C.FIELD_REQUESTED_BY_NAME] && (
        <>
          <span className="font-weight-normal">
            {' '}
            <I18n>{sameByAndFor ? 'by and for' : 'by'}</I18n>{' '}
          </span>
          <span>{submission.values[C.FIELD_REQUESTED_BY_NAME]}</span>
        </>
      )}
      {!sameByAndFor &&
        !!submission.values[C.FIELD_REQUESTED_FOR_NAME] && (
          <>
            <span className="font-weight-normal">
              {' '}
              <I18n>for</I18n>{' '}
            </span>
            <span>{submission.values[C.FIELD_REQUESTED_FOR_NAME]}</span>
          </>
        )}
    </>
  );
};

const ServiceLevelTargetItem = ({
  submission,
  active = false,
  display = 'd',
  direction = 'both',
  total = false,
}) => {
  const totalSLT = Utils.getAttributeValue(submission.form, C.FORM_ATTR_SLT);
  return (
    <>
      {total && totalSLT && `${expandSLTValue(totalSLT)} | `}
      <Countdown
        date={submission.values[C.FIELD_SLT]}
        display={display}
        direction={direction}
        static={!false}
      >
        {({ content, invalid, isCompleted }) =>
          invalid ? (
            <span className="text-danger">
              <I18n>Invalid SLT Date</I18n>
            </span>
          ) : (
            <span className={classNames({ 'text-danger': isCompleted })}>
              {content}
              <span className="font-weight-normal">
                {' '}
                <I18n>{isCompleted ? 'past SLT' : 'to SLT'}</I18n>
              </span>
            </span>
          )
        }
      </Countdown>
    </>
  );
};

const buildServerNameItem = ({ submission, modal, modalOpen, modalToggle }) => {
  let serverNames = submission.values[C.FIELD_SERVER_NAMES];
  if (!Array.isArray(serverNames) || serverNames.length === 0) {
    return null;
  } else if (serverNames.length > 1) {
    if (modal) {
      return (
        <>
          <button
            className="btn btn-text"
            onClick={modalToggle}
            title="Click to see servers"
          >
            <I18n>Multiple</I18n>
            {` (${serverNames.length})`}
          </button>
          <Modal isOpen={modalOpen} toggle={modalToggle}>
            <div className="modal-header">
              <h4 className="modal-title">
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={modalToggle}
                >
                  <I18n>Close</I18n>
                </button>
                <span>
                  <I18n>Servers</I18n>
                </span>
              </h4>
            </div>
            <ModalBody>
              <div className="p-3 server-names-list">
                {serverNames.map(n => <span key={n}>{n}</span>)}
              </div>
            </ModalBody>
          </Modal>
        </>
      );
    } else {
      return (
        <>
          <I18n>Multiple</I18n>
          {` (${serverNames.length})`}
        </>
      );
    }
  } else {
    return serverNames[0];
  }
};

const buildIssuesItem = ({ submission }) => {
  const issues = Array.isArray(submission.descendants)
    ? submission.descendants.filter(
        d => d.type === 'Issue' && d.coreState === 'Draft',
      )
    : [];
  return (
    issues.length > 0 && (
      <span className="text-danger">
        <I18n>Unresolved Issue(s)</I18n>
      </span>
    )
  );
};

const RequestMetaWrapperComponent = ({ submission, children, ...props }) => {
  return children(props);
};

export const mapStateToProps = (state, props) => ({
  kapp: state.app.kapp,
  profile: state.app.profile,
  appLocation: state.app.location,
  escalateForm:
    state.app.kapp &&
    state.app.kapp.forms &&
    state.app.kapp.forms.find(f => f.slug === C.FORM_SLUG_ESCALATE),
});

export const mapDispatchToProps = {
  cloneSubmission: actions.cloneSubmissionRequest,
  deleteSubmission: actions.deleteSubmissionRequest,
  push,
};

export const RequestMetaWrapper = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  lifecycle({
    componentDidMount() {},
    componentWillUnmount() {},
  }),
  withProps(({ submission, profile, navigate, escalateForm }) => {
    const trackedCloneLabel = Utils.getAttributeValue(
      submission ? submission.form : {},
      C.FORM_ATTR_TRACKED_DUPLICATE_BUTTON,
      null,
    );
    const pvgOverride = !!submission
      ? parsePVGOverrideAttribute(
          Utils.getAttributeValue(submission.form, C.FORM_ATTR_PVG_OVERRIDE),
          profile,
          !submission.values[C.FIELD_PVG_OVERRIDE_DEFERRAL],
        )
      : { valid: false };
    return {
      navigate: navigate || push,
      canAddComment:
        !!submission && submission.coreState !== C.CORE_STATE_DRAFT,
      canClone:
        !!submission &&
        C.REQUEST_FORM_STATUSES.includes(submission.form.status) &&
        Utils.hasAttributeValue(
          submission.form,
          C.FORM_ATTR_CLONEABLE,
          ['true', 'yes'],
          true,
        ) &&
        !submission.values[C.FIELD_IS_LEGACY],
      canCloneWithTracking:
        !!submission &&
        !!trackedCloneLabel &&
        submission.coreState === C.CORE_STATE_CLOSED,
      trackedCloneLabel,
      canConfirm:
        !!submission &&
        submission.coreState === C.CORE_STATE_SUBMITTED &&
        ['No', 'No 1'].includes(submission.values[C.FIELD_CONFIRM_SUCCESS]),
      canEscalate:
        escalateForm &&
        !!submission &&
        submission.coreState === C.CORE_STATE_SUBMITTED &&
        !Utils.hasAttributeValue(
          submission.form,
          C.FORM_ATTR_EXCLUDE_ESCALATE_FORM,
          ['true', 'yes'],
          true,
        ),
      canAddIssue:
        !!submission &&
        submission.coreState === C.CORE_STATE_SUBMITTED &&
        !Utils.hasAttributeValue(
          submission.form,
          C.FORM_ATTR_EXCLUDE_ISSUE_BUTTON,
          ['true', 'yes'],
          true,
        ),
      canCancel:
        !!submission &&
        submission.coreState !== C.CORE_STATE_CLOSED &&
        !Utils.hasAttributeValue(
          submission.form,
          C.FORM_ATTR_CANCELABLE,
          ['false', 'no'],
          true,
        ),
      pvgOverride:
        !!submission &&
        submission.coreState === C.CORE_STATE_SUBMITTED &&
        pvgOverride.valid
          ? pvgOverride.formSlug
          : false,
    };
  }),
  withState('serversModalOpen', 'setServersModalOpen', false),
  withHandlers(() => {
    // Handler for adding a comment by opening a modal form
    const handleAddComment = props => ({ preventDefault } = {}) =>
      props.canAddComment
        ? e => {
            if (preventDefault) e.preventDefault();
            openModalForm({
              ...C.COMMENT_FORM_CONFIG,
              kappSlug: props.kapp && props.kapp.slug,
              values: { [C.FIELD_RELATED_SUBMISSION_ID]: props.submission.id },
              duration: 5000,
            });
          }
        : undefined;
    // Handler for cloning the current submission into a new draft submission
    const handleClone = props => ({ preventDefault } = {}) =>
      props.canClone
        ? e => {
            if (preventDefault) e.preventDefault();
            props.cloneSubmission({
              id: props.submission.id,
              success: clonedSubmission =>
                props.navigate(
                  `${props.appLocation}/requests/Draft/request/${
                    clonedSubmission.id
                  }`,
                ),
            });
          }
        : undefined;
    // Handler for cloning with tracking the current submission into a new draft submission
    const handleCloneWithTracking = props => ({ preventDefault } = {}) =>
      props.canCloneWithTracking
        ? e => {
            if (preventDefault) e.preventDefault();
            props.cloneSubmission({
              id: props.submission.id,
              track: true,
              success: clonedSubmission =>
                props.navigate(
                  `${props.appLocation}/requests/Draft/request/${
                    clonedSubmission.id
                  }`,
                ),
            });
          }
        : undefined;
    // Handler for cancelling a submission
    const handleCancel = props => ({ preventDefault, success } = {}) =>
      props.canCancel
        ? e => {
            if (preventDefault) e.preventDefault();
            if (props.submission.coreState === 'Draft') {
              props.deleteSubmission({
                id: props.submission.id,
                success,
              });
            } else {
              openModalForm({
                ...C.CANCEL_FORM_CONFIG,
                kappSlug: props.kapp && props.kapp.slug,
                formSlug: Utils.getAttributeValue(
                  props.submission.form,
                  C.FORM_ATTR_CANCEL_TEMPLATE,
                  C.FORM_SLUG_CANCEL,
                ),
                values: {
                  [C.FIELD_RELATED_SUBMISSION_ID]: props.submission.id,
                },
                duration: 5000,
              });
            }
          }
        : undefined;
    // Handler for confirming success of a request
    const handleConfirmSuccess = props => ({ preventDefault } = {}) =>
      props.canConfirm
        ? e => {
            if (preventDefault) e.preventDefault();
            openModalForm({
              ...C.CONFIRM_SUCCESS_FORM_CONFIG,
              kappSlug: props.kapp && props.kapp.slug,
              values: {
                [C.FIELD_RELATED_SUBMISSION_ID]: props.submission.id,
              },
              duration: 5000,
            });
          }
        : undefined;
    // Handler for escalating a request
    const handleEscalate = props => ({ preventDefault, init } = {}) =>
      props.canEscalate
        ? e => {
            if (preventDefault) e.preventDefault();
            if (typeof init === 'function') init();
            openModalForm({
              ...C.ESCALATE_FORM_CONFIG,
              kappSlug: props.kapp && props.kapp.slug,
              values: {
                [C.FIELD_RELATED_SUBMISSION_ID]: props.submission.id,
              },
              duration: 5000,
            });
          }
        : undefined;
    // Handler for pvg override
    const handlePVGOverride = props => ({ preventDefault } = {}) =>
      props.pvgOverride
        ? e => {
            if (preventDefault) e.preventDefault();
            openModalForm({
              ...C.PVG_OVERRIDE_FORM_CONFIG,
              kappSlug: props.kapp && props.kapp.slug,
              formSlug: props.pvgOverride,
              values: {
                [C.FIELD_RELATED_SUBMISSION_ID]: props.submission.id,
                [C.FIELD_RELATED_DEFERRAL_TOKEN]:
                  props.submission.values[C.FIELD_PVG_OVERRIDE_DEFERRAL] || '',
              },
              duration: 5000,
            });
          }
        : undefined;
    // Handler for adding issues
    const handleAddIssue = props => ({ preventDefault } = {}) =>
      props.canAddIssue
        ? e => {
            if (preventDefault) e.preventDefault();
            openModalForm({
              ...C.ISSUE_CREATE_FORM_CONFIG,
              kappSlug: props.kapp && props.kapp.slug,
              values: {
                [C.FIELD_RELATED_SUBMISSION_ID]: props.submission.id,
              },
              duration: 5000,
            });
          }
        : undefined;

    return {
      handleAddComment,
      handleClone,
      handleCloneWithTracking,
      handleCancel,
      handleConfirmSuccess,
      handleEscalate,
      handlePVGOverride,
      handleAddIssue,
      serversModalToggle: props => () => props.setServersModalOpen(o => !o),
    };
  }),
  withHandlers(() => {
    return {
      showIdList: ({ submission }) => () =>
        [
          submission.handle,
          ...(submission.values[C.FIELD_TICKET_NUMBERS] || []),
        ].filter(Boolean),
      showDisplayDate: ({ submission }) => () => (
        <DisplayDateItem submission={submission} />
      ),
      showSLT: ({ submission }) => (options = {}) =>
        submission.coreState === C.CORE_STATE_SUBMITTED &&
        !!submission.values[C.FIELD_SLT] &&
        !['Completed', 'Resolved'].includes(
          submission.values[C.FIELD_STATUS],
        ) && <ServiceLevelTargetItem {...options} submission={submission} />,
      showClosedDate: ({ submission }) => () =>
        submission.coreState === C.CORE_STATE_CLOSED && (
          <TimeAgo timestamp={submission.closedAt} />
        ),
      showTracking: ({ submission }) => () =>
        !!submission.values[C.FIELD_TRACKING_NUMBER] &&
        submission.values[C.FIELD_TRACKING_NUMBER],
      showServerNames: ({
        submission,
        serversModalOpen,
        serversModalToggle,
      }) => ({ modal } = {}) =>
        buildServerNameItem({
          submission,
          modal,
          modalOpen: serversModalOpen,
          modalToggle: serversModalToggle,
        }),
      showIssues: ({ submission }) => () => buildIssuesItem({ submission }),
    };
  }),
)(RequestMetaWrapperComponent);
